import React, { useState, useEffect, useCallback } from 'react';
import { Input, Select, InputNumber, Button, Card, Row, Col, message, Steps,
  Table, Typography, Alert, Space, Divider, Tag, Spin, Upload } from 'antd';
import { ArrowLeftOutlined, SaveOutlined, PlusOutlined, DeleteOutlined,
  UploadOutlined, StarFilled, StarOutlined, CalculatorOutlined, FilePdfOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../services/api';
import useAvisoAlteracoes from '../hooks/useAvisoAlteracoes';
import CabecalhoLaudo from '../components/CabecalhoLaudo';

const { Text } = Typography;
const { Option } = Select;
const TIPO_LAUDO_FIXO = 'Inspeção NR13 - Tubulação';
const GERATRIZES = ['A', 'B', 'C', 'D'];
const UNI_P = [['kgf', 'kgf/cm²'], ['psi', 'psi'], ['bar', 'bar'], ['mpa', 'MPa']];
const JUNTA_LONG = [
  ['sem costura', 'Sem costura (E = 1,00)'],
  ['erw', 'ERW — resistência elétrica (E = 0,85)'],
  ['fusao eletrica dupla solda', 'Fusão elétrica, dupla solda (E = 0,85)'],
  ['fusao eletrica dupla solda com radiografia total', 'Fusão elétrica com radiografia total (E = 1,00)'],
  ['soldado por forno', 'Soldado por forno (E = 0,60)']
];

const num = (v) => (v === null || v === undefined || v === '' ? null : Number(v));
const fmt = (n, c) => (n === null || n === undefined || !isFinite(Number(n))
  ? '—' : Number(n).toFixed(c).replace('.', ','));
// O mesmo valor em todas as unidades que aparecem nos laudos antigos
const emTodas = (mpa) => {
  const n = Number(mpa);
  if (!isFinite(n)) return '—';
  return fmt(n, 3) + ' MPa  ·  ' + fmt(n / 0.0980665, 2) + ' kgf/cm²  ·  ' +
    fmt(n * 10, 1) + ' bar  ·  ' + fmt(n / 0.00689476, 0) + ' psi';
};

const paraKgf = (mpa) => { const n = Number(mpa); return isFinite(n) ? (n / 0.0980665).toFixed(0) : ''; };
// O cadastro guarda o tipo em minusculo com underline; na tela vai por extenso
const ROTULO_PADRAO = {
  medidor_espessura: 'Medidor de espessura', manometro: 'Manômetro',
  manovacuometro: 'Manovacuômetro', termometro: 'Termômetro', incubadora: 'Incubadora'
};
const nomePadrao = (t) => ROTULO_PADRAO[t] ||
  String(t || '').replace(/_/g, ' ').replace(/^./, (c) => c.toUpperCase());
// O servidor guarda tudo em MPa; a unidade e so de exibicao.
const FATOR_P = { mpa: 1, kgf: 0.0980665, bar: 0.1, psi: 0.00689476 };
const naUnidade = (mpa, uni) => {
  const n = Number(mpa);
  if (!isFinite(n)) return null;
  return n / (FATOR_P[String(uni || 'kgf').toLowerCase()] || 1);
};
const rotuloUni = (uni) => (UNI_P.find((x) => x[0] === (uni || 'kgf')) || ['', 'kgf/cm²'])[1];
// Os mais usados vao para o topo: a lista tem 26 materiais, e rolar ate o certo
// a cada trecho e onde se erra o material sem perceber.
const MATERIAIS_NO_TOPO = ['ASTM A 36'];
const ordenarMateriais = (lista) => {
  const topo = [], resto = [];
  (lista || []).forEach((m) => (MATERIAIS_NO_TOPO.indexOf(m.nome) >= 0 ? topo : resto).push(m));
  topo.sort((a, b) => MATERIAIS_NO_TOPO.indexOf(a.nome) - MATERIAIS_NO_TOPO.indexOf(b.nome));
  return topo.concat(resto);
};

const aj = (t) => <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 2, lineHeight: 1.3 }}>{t}</div>;

export default function InspecaoTubulacao() {
  const nav = useNavigate();
  const { id } = useParams();

  const [d, setD] = useState(null);
  const naoSalvo = useAvisoAlteracoes(d);
  const [trechos, setTrechos] = useState([]);
  const [step, setStep] = useState(0);
  const [salvando, setSalvando] = useState(false);
  const [bitolas, setBitolas] = useState([]);
  const [materiais, setMateriais] = useState([]);
  const [juntas, setJuntas] = useState([]);
  const [fluidos, setFluidos] = useState([]);
  const [padroes, setPadroes] = useState([]);
  const [avisos, setAvisos] = useState([]);
  const [imgs, setImgs] = useState({});
  const [croq, setCroq] = useState({});
  const [enviando, setEnviando] = useState(false);

  const set = (campo, v) => setD((x) => ({ ...x, [campo]: v }));

  const filaMini = React.useRef({ ativos: 0, espera: [], feitas: {} });
  const miniatura = useCallback(async (tipo, idx, arq) => {
    const q = filaMini.current;
    const chave = tipo + ':' + (arq || idx);
    if (arq && q.feitas[chave]) return;
    while (q.ativos >= 4) await new Promise((ok) => q.espera.push(ok));
    q.ativos++;
    try {
      const r = await api.get('/inspecoes-tubulacao/' + id + '/' + tipo + '/' + idx + '?mini=1', { responseType: 'blob' });
      if (arq) q.feitas[chave] = true;
      const url = URL.createObjectURL(r.data);
      if (tipo === 'fotos') setImgs((p) => ({ ...p, [idx]: url }));
      else setCroq((p) => ({ ...p, [idx]: url }));
    } catch (e) { /* miniatura fica vazia */ }
    finally { q.ativos--; const nx = q.espera.shift(); if (nx) nx(); }
  }, [id]);

  const carregar = useCallback(async () => {
    try {
      const r = await api.get('/inspecoes-tubulacao/' + id);
      const l = r.data;
      setTrechos((l.trechos || []).map((t) => ({ ...t, medicoes: t.medicoes || [] })));
      delete l.trechos;
      // O padrao do Select e so visual: se nao entrar em d, o salvamento manda
      // vazio e apaga a unidade no banco — e o calculo passa a assumir MPa.
      ['pmo_unidade', 'pmta_unidade', 'pth_unidade', 'teste_pressao_unidade']
        .forEach((k) => { if (!l[k]) l[k] = 'kgf'; });
      if (!l.status) l.status = 'rascunho';
      setD(l);
      (l.fotos || []).forEach((f, i) => miniatura('fotos', i, i + ':' + (f.arquivo || f.caminho)));
      (l.croquis || []).forEach((f, i) => miniatura('croquis', i, i + ':' + (f.arquivo || f.caminho)));
    } catch { message.error('Erro ao carregar o laudo'); }
  }, [id, miniatura]);

  useEffect(() => { carregar(); }, [carregar]);
  useEffect(() => {
    api.get('/inspecoes-tubulacao/apoio/bitolas').then((r) => setBitolas(r.data || [])).catch(() => {});
    api.get('/inspecoes-tubulacao/apoio/materiais').then((r) => setMateriais(r.data || [])).catch(() => {});
    api.get('/inspecoes-tubulacao/apoio/juntas').then((r) => setJuntas(r.data || [])).catch(() => {});
    api.get('/inspecoes-tubulacao/apoio/padroes').then((r) => setPadroes(r.data || [])).catch(() => {});
    api.get('/classificacao-nr13/fluidos').then((r) => setFluidos(r.data || [])).catch(() => {});
  }, []);

  // ------------------------------------------------------------ imagens
  // Depois de salvar, volta so o que o servidor calcula. Recarregar o laudo
  // inteiro passava por cima do que o usuario digitou enquanto o salvamento
  // estava em andamento — e era isso que apagava campo preenchido.
  const recarregarCalculados = useCallback(async () => {
    try {
      const r = await api.get('/inspecoes-tubulacao/' + id);
      const l = r.data;
      setTrechos((l.trechos || []).map((t) => ({ ...t, medicoes: t.medicoes || [] })));
      setD((x) => ({ ...x,
        codigo: l.codigo, revisao: l.revisao, status: l.status || x.status,
        pmta_calculada: l.pmta_calculada, atualizado_em: l.atualizado_em,
        fotos: l.fotos, croquis: l.croquis }));
      (l.fotos || []).forEach((f, i) => miniatura('fotos', i, i + ':' + (f.arquivo || f.caminho)));
      (l.croquis || []).forEach((f, i) => miniatura('croquis', i, i + ':' + (f.arquivo || f.caminho)));
    } catch { /* mantem o que esta na tela */ }
  }, [id, miniatura]);

  const subir = (tipo) => async (file) => {
    const fd = new FormData(); fd.append('foto', file);
    setEnviando(true);
    try { await api.post('/inspecoes-tubulacao/' + id + '/' + tipo, fd); await recarregarCalculados(); }
    catch (e) { message.error('Erro ao enviar a imagem'); }
    setEnviando(false);
    return false;
  };
  const remover = (tipo) => async (idx) => {
    try {
      await api.delete('/inspecoes-tubulacao/' + id + '/' + tipo + '/' + idx);
      if (tipo === 'fotos') setImgs({}); else setCroq({});
      await recarregarCalculados();
    } catch (e) { message.error('Erro ao remover'); }
  };
  const definirCapa = async (idx) => {
    try { await api.put('/inspecoes-tubulacao/' + id + '/fotos/' + idx + '/principal'); await recarregarCalculados(); }
    catch (e) { message.error('Erro ao definir a capa'); }
  };

  // ------------------------------------------------------------ trechos
  const novoTrecho = () => setTrechos((ts) => ts.concat([{
    ordem: ts.length, descricao: '', dn: null, sch: null,
    junta_longitudinal: 'sem costura', junta_montagem: 'Roscada NPT',
    sobre_corrosao_mm: 0.5, medicoes: []
  }]));

  const mudaTrecho = (i, campo, v) => setTrechos((ts) => {
    const n = ts.slice(); n[i] = { ...n[i], [campo]: v };
    // diametro externo e parede nominal vem da tabela de bitolas; a parede
    // continua editavel porque o tubo instalado pode nao ser o da tabela
    if (campo === 'dn' || campo === 'sch') {
      const b = bitolas.find((x) => x.dn === (campo === 'dn' ? v : n[i].dn)
        && x.sch === (campo === 'sch' ? v : n[i].sch));
      if (b) { n[i].od_mm = Number(b.od_mm); n[i].parede_nominal_mm = Number(b.parede_mm); }
    }
    if (campo === 'material_tensao_id') {
      const m = materiais.find((x) => x.id === v);
      if (m) {
        n[i].material_nome = m.nome;
        n[i].tensao_mpa = Number(m.tensao_mpa);
        n[i].tensao_norma = m.norma_confirmada ? m.norma : null;
      }
    }
    return n;
  });

  const removeTrecho = (i) => setTrechos((ts) => ts.filter((_, k) => k !== i));

  const pontosDe = (t) => {
    const max = (t.medicoes || []).reduce((a, m) => Math.max(a, Number(m.ponto) || 0), 0);
    return Array.from({ length: max }, (_, k) => k + 1);
  };
  const addPonto = (i) => setTrechos((ts) => {
    const n = ts.slice(); const t = { ...n[i] };
    const p = pontosDe(t).length + 1;
    t.medicoes = (t.medicoes || []).concat(
      GERATRIZES.map((g) => ({ ponto: p, geratriz: g, valor_mm: null })));
    n[i] = t; return n;
  });
  const setMedicao = (i, ponto, g, v) => setTrechos((ts) => {
    const n = ts.slice(); const t = { ...n[i] };
    t.medicoes = (t.medicoes || []).map((m) =>
      (Number(m.ponto) === ponto && m.geratriz === g) ? { ...m, valor_mm: v } : m);
    n[i] = t; return n;
  });
  const valorMed = (t, ponto, g) => {
    const m = (t.medicoes || []).find((x) => Number(x.ponto) === ponto && x.geratriz === g);
    return m ? m.valor_mm : null;
  };

  // ------------------------------------------------------------ salvar
  const salvar = async (aviso = true) => {
    if (!d) return;
    setSalvando(true);
    try {
      const corpo = { ...d };
      ['id', 'codigo', 'criado_em', 'atualizado_em', 'disponivel_portal', 'app_uuid',
        'razao_social', 'nome_cliente', 'cnpj', 'art_cliente', 'cnpj_faturamento',
        'os_numero', 'end_atend_logradouro', 'end_atend_numero', 'end_atend_bairro',
        'end_atend_cidade', 'end_atend_estado', 'end_atend_cep', 'fotos', 'croquis']
        .forEach((k) => delete corpo[k]);
      corpo.tipo_laudo = TIPO_LAUDO_FIXO;
      corpo.trechos = trechos.map((t, k) => ({ ...t, ordem: k }));
      const r = await api.put('/inspecoes-tubulacao/' + id, corpo);
      const calc = r.data.calculo || null;
      setAvisos((calc && calc.avisos) || []);
      await recarregarCalculados();
      naoSalvo.limpo();
      if (aviso) message.success('Laudo salvo');
      return calc;
    } catch (e) {
      message.error('Erro ao salvar: ' + (e?.response?.data?.erro || e.message));
      return null;
    } finally { setSalvando(false); }
  };

  // Abre o PDF numa aba, sem baixar. O servidor recalcula antes de imprimir.
  const gerarPdf = async () => {
    message.loading({ content: 'Gerando o PDF...', key: 'pdf', duration: 0 });
    try {
      await salvar(false);
      const r = await api.get('/inspecoes-tubulacao/' + id + '/pdf', { responseType: 'blob' });
      message.destroy('pdf');
      window.open(URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' })), '_blank');
      await recarregarCalculados();
    } catch (e) {
      message.destroy('pdf');
      let msg = 'Erro ao gerar o PDF';
      try { msg = JSON.parse(await e.response.data.text()).erro || msg; } catch (x) { /* resposta nao e json */ }
      message.error(msg);
    }
  };

  // Teste hidrostatico pelo ASME B31.3, item 345.4.2: 1,5 vez a pressao de
  // projeto. O fator do vaso de pressao e outro, da ASME Secao VIII — por isso
  // nao se aproveita o numero de um laudo no outro.
  const calcularPth = () => {
    const p = Number(d.pmta);
    if (!isFinite(p) || p <= 0) {
      message.warning('Preencha a PMTA adotada primeiro: o teste sai dela.');
      return;
    }
    set('pth', Math.round(p * 1.5 * 100) / 100);
    message.success('Pressão de teste: 1,5 × PMTA adotada (ASME B31.3, 345.4.2).');
  };

  // O calculo precisa dos trechos gravados, entao grava e recalcula de uma vez.
  const calcularPmta = async () => {
    const c = await salvar(false);
    if (c && c.pmta_calculada !== null && c.pmta_calculada !== undefined) {
      message.success({
        content: 'PMTA calculada: ' + fmt(naUnidade(c.pmta_calculada, d.pmta_unidade), 2) +
          ' ' + rotuloUni(d.pmta_unidade) + ' — é o teto do trecho mais fraco.',
        duration: 6
      });
    } else {
      message.warning('Não deu para calcular. Veja os pontos de atenção no topo da tela.');
    }
  };

  if (!d) return <div style={{ padding: 40, textAlign: 'center' }}><Spin /></div>;

  const congelado = !!(d.codigo || d.status === 'concluido');
  const teto = d.pmta_calculada === null || d.pmta_calculada === undefined
    ? null : Number(d.pmta_calculada);

  const simNao = (campo, rotulo, ajuda, span = 5) => (
    <Col span={span}>
      <label>{rotulo}</label>
      <Select style={{ width: '100%' }} allowClear placeholder="—"
        value={d[campo] === true ? true : d[campo] === false ? false : undefined}
        onChange={(v) => set(campo, v === undefined ? null : v)}
        options={[{ value: true, label: 'Sim' }, { value: false, label: 'Não' }]} />
      {ajuda ? aj(ajuda) : null}
    </Col>
  );

  const etapas = [{ title: 'Identificação' }, { title: 'A linha' }, { title: 'Trechos e PMTA' },
    { title: 'Dispositivos' }, { title: 'Teste' }, { title: 'Fotos e croqui' }, { title: 'Resultado' }];

  return (
    <div style={{ padding: 16 }}>
      <Space style={{ marginBottom: 12 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => nav('/laudos')}>Voltar</Button>
        <b>{TIPO_LAUDO_FIXO}</b>
        {d.codigo && <Tag color="blue">{d.codigo}</Tag>}
      </Space>

      <Steps current={step} size="small" items={etapas} onChange={setStep} style={{ marginBottom: 16 }} />

      {avisos.length > 0 &&
        <Alert type="warning" showIcon style={{ marginBottom: 12 }}
          message="Pontos de atenção do cálculo"
          description={<ul style={{ margin: 0, paddingLeft: 18 }}>
            {avisos.map((a, k) => <li key={k}>{a}</li>)}</ul>} />}

      {step === 0 && <CabecalhoLaudo d={d} set={set} tipoLaudo={TIPO_LAUDO_FIXO} />}

      {step === 1 && (
        <Card size="small" title="A linha">
          <Row gutter={16}>
            <Col span={5}><label>TAG</label>
              <Input value={d.tag || ''} onChange={(e) => set('tag', e.target.value)} />
              {aj('Identificação da linha. É o que a NR-13 13.6.2.7 exige.')}</Col>
            <Col span={6}><label>Fluido</label>
              <Select style={{ width: '100%' }} showSearch optionFilterProp="children"
                value={d.fluido || undefined}
                onChange={(v) => {
                  // 'Outro' nao tem cadastro: a classe fica em branco para o
                  // responsavel tecnico escolher, em vez de herdar a do fluido anterior
                  const f = fluidos.find((x) => x.nome === v);
                  setD((x) => ({ ...x, fluido: v, classe_fluido: f ? f.classe : null }));
                }}>
                {fluidos.map((f) => (
                  <Option key={f.id} value={f.nome}>{f.nome} (Classe {f.classe})</Option>
                ))}
                <Option value="outro">Outro</Option>
              </Select>
              {aj('A classe vem junto do cadastro de fluidos.')}</Col>
            {d.fluido === 'outro' && (
              <Col span={5}><label>Fluido (outro)</label>
                <Input value={d.fluido_outro || ''} onChange={(e) => set('fluido_outro', e.target.value)} /></Col>
            )}
            <Col span={3}><label>Classe do fluido</label>
              <Select style={{ width: '100%' }} value={d.classe_fluido || undefined}
                disabled={d.fluido !== 'outro'} placeholder="—"
                onChange={(v) => set('classe_fluido', v)}
                options={['A', 'B', 'C', 'D'].map((c) => ({ value: c, label: c }))} />
              {aj(d.fluido === 'outro'
                ? 'Fluido fora do cadastro: escolha a classe conforme a NR-13.'
                : 'Vem junto do cadastro de fluidos.')}</Col>
            <Col span={5}><label>Tipo de inspeção</label>
              <Select style={{ width: '100%' }} value={d.tipo_inspecao_periodicidade || undefined}
                onChange={(v) => set('tipo_inspecao_periodicidade', v)}
                options={[{ value: 'inicial', label: 'Inicial' }, { value: 'periodica', label: 'Periódica' },
                  { value: 'extraordinaria', label: 'Extraordinária' }]} />
              {aj('Extraordinária: dano, reparo ou parada acima de 12 meses (13.6.2.4).')}</Col>
          </Row>

          <Row gutter={16} style={{ marginTop: 14 }}>
            <Col span={4}><label>Temp. mínima (°C)</label>
              <InputNumber decimalSeparator="," style={{ width: '100%' }} value={num(d.temperatura_min)}
                onChange={(v) => set('temperatura_min', v)} /></Col>
            <Col span={4}><label>Temp. máxima (°C)</label>
              <InputNumber decimalSeparator="," style={{ width: '100%' }} value={num(d.temperatura_max)}
                onChange={(v) => set('temperatura_max', v)} />
              {aj('Acima de 482 °C o cálculo trava: a tensão do cadastro vale a 20 °C.')}</Col>
            <Col span={4}><label>PMO</label>
              <InputNumber decimalSeparator="," style={{ width: '100%' }} value={num(d.pmo)} onChange={(v) => set('pmo', v)} />
              {aj('Pressão máxima de operação da linha.')}</Col>
            <Col span={3}><label>Unidade</label>
              <Select style={{ width: '100%' }} value={d.pmo_unidade || 'kgf'}
                onChange={(v) => set('pmo_unidade', v)}
                options={UNI_P.map(([v, l]) => ({ value: v, label: l }))} /></Col>
            <Col span={4}><label>Extensão (m)</label>
              <InputNumber decimalSeparator="," style={{ width: '100%' }} value={num(d.extensao_m)}
                onChange={(v) => set('extensao_m', v)} /></Col>
          </Row>

          <Row gutter={16} style={{ marginTop: 14 }}>
            <Col span={8}><label>Local de instalação</label>
              <Input value={d.local_instalacao || ''} onChange={(e) => set('local_instalacao', e.target.value)} /></Col>
            <Col span={12}><label>Vaso ou caldeira interligado</label>
              <Input value={d.equipamento_interligado || ''}
                onChange={(e) => set('equipamento_interligado', e.target.value)} />
              {aj('Pela NR-13 13.6.2.2, o prazo da tubulação segue o do vaso ou caldeira mais crítico da linha.')}</Col>
          </Row>

          <Alert type="info" showIcon style={{ marginTop: 14 }}
            message="A PMTA fica na próxima etapa, depois das medições — é o cálculo que mostra o teto antes de você adotar o valor." />
        </Card>
      )}

      {step === 2 && (
        <>
          {trechos.map((t, i) => (
            <Card key={i} size="small" style={{ marginBottom: 12 }}
              title={'Trecho ' + (i + 1) + (t.dn ? ' — ' + t.dn + ' ' + (t.sch || '') : '')}
              extra={<Button danger size="small" icon={<DeleteOutlined />}
                onClick={() => removeTrecho(i)}>Remover</Button>}>
              <Row gutter={12}>
                <Col span={6}><label>Trecho medido</label>
                  <Input value={t.descricao || ''} placeholder="Primeiros 50 m, do registro até a cozinha..."
                    onChange={(e) => mudaTrecho(i, 'descricao', e.target.value)} />
                  {aj('Onde ao longo da tubulação fica este trecho. O local da instalação já está na etapa A linha.')}</Col>
                <Col span={3}><label>Diâmetro</label>
                  <Select style={{ width: '100%' }} value={t.dn || undefined}
                    onChange={(v) => mudaTrecho(i, 'dn', v)}
                    options={[...new Set(bitolas.map((b) => b.dn))].map((x) => ({ value: x, label: x }))} /></Col>
                <Col span={3}><label>Schedule</label>
                  <Select style={{ width: '100%' }} value={t.sch || undefined}
                    onChange={(v) => mudaTrecho(i, 'sch', v)}
                    options={[...new Set(bitolas.filter((b) => b.dn === t.dn).map((b) => b.sch))]
                      .map((x) => ({ value: x, label: x }))} /></Col>
                <Col span={3}><label>Ø externo (mm)</label>
                  <InputNumber decimalSeparator="," style={{ width: '100%' }} value={num(t.od_mm)} disabled />
                  {aj('Da tabela ASME B36.10M.')}</Col>
                <Col span={3}><label>Parede nominal</label>
                  <InputNumber decimalSeparator="," style={{ width: '100%' }} step={0.01} value={num(t.parede_nominal_mm)}
                    onChange={(v) => mudaTrecho(i, 'parede_nominal_mm', v)} />
                  {aj('Vem da tabela; corrija se o tubo instalado for outro.')}</Col>
                <Col span={6}><label>Material</label>
                  <Select style={{ width: '100%' }} showSearch optionFilterProp="label"
                    value={t.material_tensao_id || undefined}
                    onChange={(v) => mudaTrecho(i, 'material_tensao_id', v)}
                    options={ordenarMateriais(materiais).map((m) => ({ value: m.id,
                      label: m.nome + ' — ' + m.tensao_mpa + ' MPa' }))} /></Col>
              </Row>

              <Row gutter={12} style={{ marginTop: 10 }}>
                <Col span={4}><label>Tensão admissível</label>
                  <Input disabled value={t.tensao_mpa
                    ? Number(t.tensao_mpa).toFixed(2) + ' MPa' : ''} />
                  {aj(t.tensao_mpa ? paraKgf(t.tensao_mpa) + ' kgf/cm²' : 'Escolha o material')}</Col>
                <Col span={6}><label>Junta longitudinal</label>
                  <Select style={{ width: '100%' }} value={t.junta_longitudinal || undefined}
                    onChange={(v) => mudaTrecho(i, 'junta_longitudinal', v)}
                    options={JUNTA_LONG.map(([v, l]) => ({ value: v, label: l }))} />
                  {aj('Define o fator E. Tubo sem costura é o mais comum.')}</Col>
                <Col span={4}><label>Junta de montagem</label>
                  <Select style={{ width: '100%' }} value={t.junta_montagem || undefined}
                    onChange={(v) => mudaTrecho(i, 'junta_montagem', v)}
                    options={juntas.map((j) => ({ value: j.tipo, label: j.tipo }))} />
                  {aj('Roscada desconta a profundidade do filete.')}</Col>
                <Col span={3}><label>Sobreesp. corrosão</label>
                  <InputNumber decimalSeparator="," style={{ width: '100%' }} step={0.1} value={num(t.sobre_corrosao_mm)}
                    onChange={(v) => mudaTrecho(i, 'sobre_corrosao_mm', v)} />
                  {aj('Em mm.')}</Col>
                <Col span={3}><label>Folga mecânica</label>
                  <InputNumber decimalSeparator="," style={{ width: '100%' }} step={0.01} placeholder="automática"
                    value={num(t.folga_mecanica_mm)}
                    onChange={(v) => mudaTrecho(i, 'folga_mecanica_mm', v)} />
                  {aj('Calculada pela bitola.')}</Col>
                <Col span={4}><label>Tempo de operação (anos)</label>
                  <InputNumber decimalSeparator="," style={{ width: '100%' }} value={num(t.anos_servico)}
                    onChange={(v) => mudaTrecho(i, 'anos_servico', v)} />
                  {aj('Há quantos anos a linha opera. Sem ele não há taxa de corrosão.')}</Col>
              </Row>

              <Divider style={{ margin: '12px 0 8px' }}>Pontos de medição (mm)</Divider>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Cada ponto é uma posição ao longo do trecho; A, B, C e D são as quatro geratrizes do tubo.
              </Text>
              <Table size="small" pagination={false} rowKey={(r) => r.ponto} style={{ marginTop: 6 }}
                dataSource={pontosDe(t).map((p) => ({ ponto: p }))}
                locale={{ emptyText: 'Nenhum ponto. Use o botão abaixo.' }}
                columns={[{ title: 'Ponto', dataIndex: 'ponto', width: 70 }].concat(
                  GERATRIZES.map((g) => ({
                    title: g, width: 100,
                    render: (_, r) => <InputNumber decimalSeparator="," size="small" step={0.1} style={{ width: '100%' }}
                      value={valorMed(t, r.ponto, g)}
                      onChange={(v) => setMedicao(i, r.ponto, g, v)} />
                  })))} />
              <Button size="small" icon={<PlusOutlined />} style={{ marginTop: 8 }}
                onClick={() => addPonto(i)}>Ponto</Button>

              {t.tm_requerida_mm !== null && t.tm_requerida_mm !== undefined && (
                <div style={{ marginTop: 12, background: '#f6ffed', padding: 8, borderRadius: 4 }}>
                  <Space size="large" wrap>
                    <span>Menor medida: <b>{t.espessura_menor_mm} mm</b></span>
                    <span>c: <b>{t.c_mm} mm</b></span>
                    <span>Mínima exigida: <b>{Number(t.tm_requerida_mm).toFixed(3)} mm</b></span>
                    <span>PMTA do trecho: <b>{Number(t.pmax_mpa).toFixed(3)} MPa</b>
                      {' '}({paraKgf(t.pmax_mpa)} kgf/cm²)</span>
                    <Tag color={t.aprovado ? 'green' : 'red'}>{t.aprovado ? 'APROVADO' : 'REPROVADO'}</Tag>
                  </Space>
                </div>
              )}
            </Card>
          ))}
          <Button type="dashed" block icon={<PlusOutlined />} onClick={novoTrecho}>Adicionar trecho</Button>

          {trechos.some((t) => t.pmax_mpa !== null && t.pmax_mpa !== undefined) && (
            <Card size="small" title="Memória de cálculo" style={{ marginTop: 14 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                ASME B31.3 — Pmax = 2 × S × E × t ÷ (D − 2 × Y × t), com t = menor espessura medida menos c,
                e c = sobreespessura de corrosão + folga da junta. Y = 0,4 para aço carbono até 480 °C.
              </Text>

              <Table size="small" pagination={false} style={{ marginTop: 10 }}
                rowKey={(r) => r.i}
                dataSource={trechos.map((t, i) => ({ i, t })).filter((x) =>
                  x.t.pmax_mpa !== null && x.t.pmax_mpa !== undefined)}
                columns={[
                  { title: 'Trecho', render: (_, r) => (r.t.dn || '') + ' ' + (r.t.sch || '') +
                    (r.t.descricao ? ' — ' + r.t.descricao : '') },
                  { title: 'Menor medida', width: 110, align: 'right',
                    render: (_, r) => fmt(r.t.espessura_menor_mm, 2) + ' mm' },
                  { title: 'c', width: 90, align: 'right',
                    render: (_, r) => fmt(r.t.c_mm, 2) + ' mm' },
                  { title: 'Mínima exigida', width: 150, align: 'right',
                    render: (_, r) => (r.t.tm_requerida_mm === null || r.t.tm_requerida_mm === undefined
                      ? <Text type="secondary" style={{ fontSize: 11 }}>preencha a PMTA e clique em Calcular</Text>
                      : fmt(r.t.tm_requerida_mm, 3) + ' mm') },
                  { title: 'PMTA calculada', width: 160, align: 'right',
                    render: (_, r) => <b>{fmt(naUnidade(r.t.pmax_mpa, d.pmta_unidade), 2)}
                      {' '}{rotuloUni(d.pmta_unidade)}</b> },
                  { title: '', width: 110, align: 'center',
                    render: (_, r) => {
                      const menor = Math.min(...trechos.filter((x) => x.pmax_mpa !== null
                        && x.pmax_mpa !== undefined).map((x) => Number(x.pmax_mpa)));
                      return Number(r.t.pmax_mpa) === menor
                        ? <Tag color="orange">gargalo</Tag> : null;
                    } },
                ]} />

              <Divider style={{ margin: '14px 0 10px' }}>Conta de cada trecho</Divider>
              {trechos.map((t, i) => {
                if (t.pmax_mpa === null || t.pmax_mpa === undefined) return null;
                const tLiq = Number(t.espessura_menor_mm) - Number(t.c_mm);
                return (
                  <div key={i} style={{ marginBottom: 14, paddingBottom: 10,
                    borderBottom: i < trechos.length - 1 ? '1px solid #f0f0f0' : 'none' }}>
                    <b>{(t.dn || '') + ' ' + (t.sch || '')}{t.descricao ? ' — ' + t.descricao : ''}</b>
                    <div style={{ fontSize: 12, color: '#595959', marginTop: 4 }}>
                      D = {fmt(t.od_mm, 2)} mm · S = {fmt(t.tensao_mpa, 2)} MPa ({paraKgf(t.tensao_mpa)} kgf/cm²)
                      {' '}· E = {fmt(t.fator_e, 2)} · Y = 0,4
                    </div>
                    <div style={{ fontSize: 12, color: '#595959' }}>
                      c = {fmt(t.sobre_corrosao_mm, 2)} de corrosão + {fmt(t.folga_mecanica_mm, 2)} de folga
                      {' '}= {fmt(t.c_mm, 2)} mm · parede resistente t = {fmt(t.espessura_menor_mm, 2)} − {fmt(t.c_mm, 2)}
                      {' '}= <b>{fmt(tLiq, 3)} mm</b>
                    </div>
                    <div style={{ fontFamily: 'monospace', fontSize: 12, marginTop: 4 }}>
                      Pmax = 2 × {fmt(t.tensao_mpa, 2)} × {fmt(t.fator_e, 2)} × {fmt(tLiq, 3)} ÷
                      {' '}({fmt(t.od_mm, 2)} − 2 × 0,4 × {fmt(tLiq, 3)})
                    </div>
                    <div style={{ marginTop: 4, color: '#135200' }}>{emTodas(t.pmax_mpa)}</div>
                    {t.taxa_corrosao_mm_ano ? (
                      <div style={{ fontSize: 12, color: '#595959', marginTop: 4 }}>
                        Taxa de corrosão {fmt(t.taxa_corrosao_mm_ano, 4)} mm/ano
                        {t.vida_remanescente_anos !== null && t.vida_remanescente_anos !== undefined
                          ? ' · vida remanescente ' + fmt(t.vida_remanescente_anos, 1) + ' anos' : ''}
                      </div>
                    ) : null}
                  </div>
                );
              })}

              {teto !== null && (
                <Alert type="success" showIcon style={{ marginTop: 6 }}
                  message={'PMTA do sistema: ' + emTodas(d.pmta_calculada)}
                  description="É a menor entre os trechos — a linha só pode operar na pressão que o pedaço mais fraco suporta." />
              )}
            </Card>
          )}

          <Card size="small" title="PMTA" style={{ marginTop: 14 }}>
            <Row gutter={16}>
              <Col span={6}><label>PMTA calculada — teto</label>
                <Input disabled value={teto === null ? '—'
                  : fmt(naUnidade(teto, d.pmta_unidade), 2) + ' ' + rotuloUni(d.pmta_unidade)}
                  style={{ background: '#f6ffed', color: '#135200', fontWeight: 'bold' }} />
                {aj('Pressão máxima que a espessura medida suporta, no trecho mais fraco.')}</Col>
              <Col span={5}><label>&nbsp;</label>
                <Button type="primary" icon={<CalculatorOutlined />} block
                  loading={salvando} onClick={calcularPmta}>Calcular</Button>
                {aj('Refaz tudo: a PMTA de cada trecho e, com a adotada preenchida, a espessura mínima exigida.')}</Col>
              <Col span={5}><label>PMTA adotada</label>
                <InputNumber decimalSeparator="," style={{ width: '100%' }} value={num(d.pmta)}
                  onChange={(v) => set('pmta', v)} />
                {aj('Só o responsável técnico preenche. Não pode passar do teto.')}</Col>
              <Col span={4}><label>Unidade</label>
                <Select style={{ width: '100%' }} value={d.pmta_unidade || 'kgf'}
                  onChange={(v) => set('pmta_unidade', v)}
                  options={UNI_P.map(([v, l]) => ({ value: v, label: l }))} /></Col>
              <Col span={5}><label>Pressão de teste hidrostático</label>
                <InputNumber decimalSeparator="," style={{ width: '100%' }} value={num(d.pth)}
                  onChange={(v) => set('pth', v)} />
                {aj('1,5 × PMTA pelo B31.3. Para GLP usa-se estanqueidade por bolha.')}</Col>
              <Col span={4}><label>&nbsp;</label>
                <Button icon={<CalculatorOutlined />} block onClick={calcularPth}>Calcular TH</Button>
                {aj('ASME B31.3, item 345.4.2.')}</Col>
            </Row>
          </Card>

        </>
      )}

      {step === 3 && (
        <Card size="small" title="Dispositivos de segurança">
          <Row gutter={16}>
            {simNao('possui_manometro', 'Possui manômetro?',
              'Atende o indicador de pressão exigido pela NR-13 13.6.1.3.')}
            <Col span={3}><label>Quantos</label>
              <InputNumber decimalSeparator="," style={{ width: '100%' }} value={num(d.qtd_manometros)}
                onChange={(v) => set('qtd_manometros', v)} /></Col>
            {simNao('possui_valvula', 'Possui válvula de segurança?',
              'Dispositivo de segurança do item 13.6.1.2.')}
            <Col span={3}><label>Quantas</label>
              <InputNumber decimalSeparator="," style={{ width: '100%' }} value={num(d.qtd_valvulas)}
                onChange={(v) => set('qtd_valvulas', v)} /></Col>
          </Row>
          <Alert type="info" showIcon style={{ marginTop: 14 }}
            message="Este laudo não gera laudo de calibração dos instrumentos — por isso aqui vai só a quantidade." />
        </Card>
      )}

      {step === 4 && (
        <Card size="small" title="Teste e instrumento">
          <Alert type="info" showIcon style={{ marginBottom: 12 }}
            message="A NR-13, item 13.6.2.1.2, permite ao profissional habilitado adotar outra técnica no lugar do teste hidrostático — é o respaldo do teste de bolha." />
          <Row gutter={16}>
            <Col span={6}><label>Método</label>
              <Select style={{ width: '100%' }} value={d.teste_metodo || undefined}
                onChange={(v) => set('teste_metodo', v)}
                options={[{ value: 'bolha', label: 'Estanqueidade por bolha' },
                  { value: 'hidrostatico', label: 'Teste hidrostático' },
                  { value: 'outro', label: 'Outro' }]} /></Col>
            <Col span={4}><label>Fluido do teste</label>
              <Input value={d.teste_fluido || ''} onChange={(e) => set('teste_fluido', e.target.value)} /></Col>
            <Col span={4}><label>Pressão</label>
              <InputNumber decimalSeparator="," style={{ width: '100%' }} value={num(d.teste_pressao)}
                onChange={(v) => set('teste_pressao', v)} /></Col>
            <Col span={3}><label>Unidade</label>
              <Select style={{ width: '100%' }} value={d.teste_pressao_unidade || 'kgf'}
                onChange={(v) => set('teste_pressao_unidade', v)}
                options={UNI_P.map(([v, l]) => ({ value: v, label: l }))} /></Col>
            <Col span={3}><label>Duração (min)</label>
              <InputNumber decimalSeparator="," style={{ width: '100%' }} value={num(d.teste_duracao_min)}
                onChange={(v) => set('teste_duracao_min', v)} /></Col>
            <Col span={4}><label>Resultado</label>
              <Select style={{ width: '100%' }} value={d.teste_resultado || undefined}
                onChange={(v) => set('teste_resultado', v)}
                options={[{ value: 'aprovado', label: 'Sem vazamentos' },
                  { value: 'reprovado', label: 'Vazamento encontrado' }]} /></Col>
          </Row>

          <Divider>Medidor de espessura</Divider>
          <Row gutter={16}>
            <Col span={10}><label>Equipamento (cadastro de padrões)</label>
              <Select style={{ width: '100%' }} allowClear value={d.medidor_padrao_tipo || undefined}
                onChange={(v) => {
                  const p = padroes.find((x) => x.tipo === v);
                  setD((x) => ({ ...x, medidor_padrao_tipo: v || null,
                    medidor_modelo: p ? (p.marca || x.medidor_modelo) : x.medidor_modelo,
                    medidor_serie: p ? (p.identificacao || x.medidor_serie) : x.medidor_serie }));
                }}
                options={padroes.map((p) => ({ value: p.tipo,
                  label: nomePadrao(p.tipo) + ' — ' + (p.identificacao || '').trim() +
                    (p.marca ? ' · ' + String(p.marca).trim() : '') +
                    (p.validade ? ' (val. ' + String(p.validade).substring(0, 10)
                      .split('-').reverse().join('/') + ')' : '') }))} />
              {aj('O certificado fica no portal do cliente, não entra no laudo.')}</Col>
            <Col span={6}><label>Modelo</label>
              <Input value={d.medidor_modelo || ''} placeholder="GM100-EM-05"
                onChange={(e) => set('medidor_modelo', e.target.value)} /></Col>
            <Col span={6}><label>Identificação / série</label>
              <Input value={d.medidor_serie || ''} onChange={(e) => set('medidor_serie', e.target.value)} /></Col>
          </Row>
          <Row gutter={16} style={{ marginTop: 12 }}>
            <Col span={8}><label>Condição da superfície</label>
              <Input value={d.condicao_superficie || ''} placeholder="laminado"
                onChange={(e) => set('condicao_superficie', e.target.value)} />
              {aj('Como o tubo estava no ponto de medição: laminado, pintado, oxidado.')}</Col>
          </Row>
        </Card>
      )}

      {step === 5 && (
        <>
          <Card size="small" title="Registro fotográfico" style={{ marginBottom: 12 }}
            extra={<Upload beforeUpload={subir('fotos')} showUploadList={false} multiple accept="image/*">
              <Button icon={<UploadOutlined />} loading={enviando}>
                {enviando ? 'Enviando...' : 'Adicionar fotos'}</Button></Upload>}>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
              {(d.fotos || []).map((f, i) => (
                <div key={i} style={{ width: 160 }}>
                  <div style={{ width: '100%', height: 120, borderRadius: 4,
                    border: f.principal ? '2px solid #1a3c5e' : '1px solid #ddd',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                    {imgs[i] ? <img alt="" src={imgs[i]}
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }} /> : <Spin size="small" />}
                  </div>
                  <Space.Compact style={{ width: '100%', marginTop: 4 }}>
                    <Button size="small" style={{ width: '55%' }}
                      icon={f.principal ? <StarFilled style={{ color: '#faad14' }} /> : <StarOutlined />}
                      onClick={() => definirCapa(i)}>Capa</Button>
                    <Button size="small" danger style={{ width: '45%' }} icon={<DeleteOutlined />}
                      onClick={() => remover('fotos')(i)} />
                  </Space.Compact>
                </div>
              ))}
              {!(d.fotos || []).length && <span style={{ color: '#888' }}>Nenhuma foto ainda.</span>}
            </div>
          </Card>

          <Card size="small" title="Croqui / projeto esquemático"
            extra={<Upload beforeUpload={subir('croquis')} showUploadList={false} multiple accept="image/*">
              <Button icon={<UploadOutlined />} loading={enviando}>Adicionar desenho</Button></Upload>}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              O desenho da linha atende o fluxograma de engenharia do item 13.6.1.4 e sai no laudo junto com a grade de medição.
            </Text>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 8 }}>
              {(d.croquis || []).map((f, i) => (
                <div key={i} style={{ width: 220 }}>
                  <div style={{ width: '100%', height: 160, border: '1px solid #ddd', borderRadius: 4,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                    {croq[i] ? <img alt="" src={croq[i]}
                      style={{ width: '100%', height: '100%', objectFit: 'contain' }} /> : <Spin size="small" />}
                  </div>
                  <Button size="small" danger block style={{ marginTop: 4 }} icon={<DeleteOutlined />}
                    onClick={() => remover('croquis')(i)}>Remover</Button>
                </div>
              ))}
              {!(d.croquis || []).length && <span style={{ color: '#888' }}>Nenhum desenho ainda.</span>}
            </div>
          </Card>
        </>
      )}

      {step === 6 && (
        <Card size="small" title="Resultado">
          {teto !== null &&
            <Alert type="success" showIcon style={{ marginBottom: 12 }}
              message={'PMTA calculada do sistema: ' + fmt(naUnidade(teto, d.pmta_unidade), 2) +
                ' ' + rotuloUni(d.pmta_unidade) + ' — quem manda é o trecho mais fraco.'} />}
          <Row gutter={16}>
            <Col span={6}><label>Parecer</label>
              <Select style={{ width: '100%' }} value={d.resultado || undefined}
                onChange={(v) => set('resultado', v)}
                options={[{ value: 'apto', label: 'Apto' },
                  { value: 'apto_com_observacoes', label: 'Apto com observações' },
                  { value: 'reprovado', label: 'Reprovado' }]} /></Col>
            <Col span={6}><label>Próxima inspeção</label>
              <Input type="date" value={d.proxima_inspecao ? String(d.proxima_inspecao).substring(0, 10) : ''}
                onChange={(e) => set('proxima_inspecao', e.target.value || null)} />
              {aj('Pela 13.6.2.2 acompanha o vaso ou caldeira mais crítico. Preenchimento manual.')}</Col>
            <Col span={6}><label>Status</label>
              <Select style={{ width: '100%' }} value={d.status || 'rascunho'}
                onChange={(v) => set('status', v)}
                options={[{ value: 'rascunho', label: 'Rascunho' },
                  { value: 'em_elaboracao', label: 'Em elaboração' },
                  { value: 'concluido', label: 'Concluído' }]} />
              {aj('Em "Concluído" o laudo vira documento e os campos travam.')}</Col>
          </Row>
          <div style={{ marginTop: 14 }}><label>Recomendações adicionais</label>
            <Input.TextArea rows={4} value={d.observacoes || ''}
              onChange={(e) => set('observacoes', e.target.value)} />
            {aj('As quatro recomendações padrão já saem no laudo. Aqui vai só o que for específico desta inspeção.')}</div>
          <div style={{ marginTop: 12 }}><label>Ação corretiva</label>
            <Input.TextArea rows={3} value={d.acao_corretiva || ''}
              onChange={(e) => set('acao_corretiva', e.target.value)} /></div>
          <div style={{ marginTop: 12 }}><label>Conclusão</label>
            <Input.TextArea rows={3} value={d.conclusao || ''}
              onChange={(e) => set('conclusao', e.target.value)} /></div>

          <Divider />
          <Button type="primary" size="large" icon={<FilePdfOutlined />}
            loading={salvando} onClick={gerarPdf}>Gerar PDF</Button>
          <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 6 }}>
            Salva, refaz os cálculos e abre o laudo numa nova aba. O código do laudo é reservado
            na primeira geração.
          </div>
        </Card>
      )}

      <Space style={{ marginTop: 16 }}>
        <Button type="primary" icon={<SaveOutlined />} loading={salvando}
          onClick={() => salvar(true)}>Salvar</Button>
        <Button disabled={step === 0} onClick={() => setStep(step - 1)}>Anterior</Button>
        <Button disabled={step === etapas.length - 1} onClick={() => setStep(step + 1)}>Próximo</Button>
      </Space>
    </div>
  );
}
